import { buildQueryString } from "./apiQueryString";
import { createReportsApi } from "./reportApi";
import { getApiBaseUrl } from '../api';
import { retryWithExponentialBackoff } from '../error-handler';
import i18n, { DEFAULT_LANGUAGE } from '@/i18n';
import type {
  Client,
  ClientSummary,
  ClientListResponse,
  Device,
  DeviceSummary,
  DeviceListResponse,
  DeviceInventoryResponse,
  User,
  UserSummary,
  AccountUsersSummary,
  PaginatedUsers,
  Alert,
  AlertsResponse,
  AlertsSummary,
  DashboardStats,
  LinkDeviceDto,
  LinkDevicesBatchDto,
  UnlinkDevicesBatchDto,
  LinkingResultDto,
  CreateClientDto,
  UpdateClientDto,
  CreateUserDto,
  UpdateUserDto,
  CreateAlertDto,
  ApiResponse,
  FilterOptions,
  InstallationToken,
  InstallationTokenListResponse,
  InstallationTokenListParams,
  InstallationTokenStatusFilter,
  CreateInstallationTokenDto,
  DeviceLicense,
  DeviceLicensesResponse
} from '../../types/types';

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

export class ApiClientError extends Error {
  status?: number;
  code?: string;
  response?: unknown;

  constructor(message: string, status?: number, code?: string, response?: unknown) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.response = response;
  }
}

class ApiClient {
  private getBaseUrl: () => string;
  private getAuthToken: (() => string | null) | null = null;
  private onTokenExpired: (() => void) | null = null;
  private defaultTimeout: number = 30000;
  private refreshTokenPromise: Promise<void> | null = null;

  constructor(getBaseUrl: () => string) {
    this.getBaseUrl = getBaseUrl;
  }

  setAuthTokenProvider(getToken: () => string | null) {
    this.getAuthToken = getToken;
  }

  setTokenExpiredHandler(handler: () => void) {
    this.onTokenExpired = handler;
  }

  private async handleRefreshToken(): Promise<void> {
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    this.refreshTokenPromise = (async () => {
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const { authService } = await import('../auth');
        await authService.refreshToken();
      } catch (error) {
        throw error;
      } finally {
        this.refreshTokenPromise = null;
      }
    })();

    return this.refreshTokenPromise;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit & {
      params?: Record<string, string>;
      timeout?: number;
      retry?: boolean;
    } = {}
  ): Promise<T> {
    const { params, timeout = this.defaultTimeout, retry = true, ...fetchOptions } = options;

    const makeRequest = async (): Promise<T> => {
      let url = `${this.getBaseUrl()}${endpoint}`;

      if (params) {
        const searchParams = new URLSearchParams(params);
        url += `?${searchParams.toString()}`;
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept-Language': i18n.language || DEFAULT_LANGUAGE,
        ...((fetchOptions.headers as Record<string, string>) ?? {}),
      };

      const token = this.getAuthToken?.();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        const response = await fetch(url, {
          ...fetchOptions,
          headers,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.status === 401 || response.status === 403) {
          let errorMessage = response.status === 401
            ? i18n.t('common:apiClient.unauthorized')
            : i18n.t('common:apiClient.forbidden');

          try {
            const errorData = await response.json();
            if (errorData?.detail) {
              errorMessage = errorData.detail;
            } else if (errorData?.error?.message) {
              errorMessage = errorData.error.message;
            } else if (errorData?.message) {
              errorMessage = errorData.message;
            }
          } catch {
          }

          const isRefreshEndpoint = endpoint.includes('/refresh');
          const isLoginEndpoint = endpoint.includes('/login');

          if (!isRefreshEndpoint && !isLoginEndpoint && response.status === 401 && retry) {
            try {
              await this.handleRefreshToken();
            } catch (refreshError) {

              if (this.onTokenExpired) {
                this.onTokenExpired();
              }

              const apiError = {
                error: {
                  message: i18n.t('common:apiClient.sessionExpired'),
                  code: 'SESSION_EXPIRED'
                },
                status: 401
              };
              throw apiError;
            }

            // Retentativa unica: 401 apos refresh bem-sucedido e erro de
            // permissao do backend, nao expiracao — cai no throw UNAUTHORIZED.
            return await this.request<T>(endpoint, { ...options, retry: false });
          }

          if (this.onTokenExpired && isRefreshEndpoint) {
            this.onTokenExpired();
          }

          const apiError = {
            error: {
              message: errorMessage,
              code: response.status === 401 ? 'UNAUTHORIZED' : 'FORBIDDEN'
            },
            status: response.status
          };
          throw apiError;
        }

        if (!response.ok) {
          let errorResponse: unknown = null;

          try {
            errorResponse = await response.json();
          } catch {
            errorResponse = {
              error: {
                message: response.statusText ?? `HTTP Error: ${response.status}`,
                code: `HTTP_${response.status}`
              },
              status: response.status
            };
          }

          throw errorResponse;
        }

        if (response.status === 204 || response.headers.get('content-length') === '0') {
          return null as T;
        }

        try {
          return await response.json() as T;
        } catch {
          throw {
            error: {
              message: i18n.t('common:apiClient.invalidResponse'),
              code: 'INVALID_RESPONSE'
            },
            status: response.status
          };
        }
      } catch (error) {
        clearTimeout(timeoutId);

        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            throw new Error(i18n.t('common:apiClient.timeout'));
          }
          if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
            throw new Error(i18n.t('common:errorHandler.networkFailure'));
          }
        }

        throw error;
      }
    };

    if (retry && (options.method === 'GET' || !options.method)) {
      return await retryWithExponentialBackoff(makeRequest, 2);
    } else {
      return await makeRequest();
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  async post<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async put<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async patch<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', ...options });
  }

  // Client endpoints
  clients = {
    getAll: (options?: FilterOptions): Promise<ApiResponse<ClientListResponse>> =>
      this.request<ApiResponse<ClientListResponse>>(`/clients${buildQueryString(options)}`),

    getById: (id: string): Promise<ApiResponse<Client>> =>
      this.request<ApiResponse<Client>>(`/clients/${id}`),

    create: (clientData: CreateClientDto): Promise<ApiResponse<Client>> =>
      this.request<ApiResponse<Client>>('/clients', {
        method: 'POST',
        body: JSON.stringify(clientData)
      }),

    update: (id: string, updates: UpdateClientDto): Promise<ApiResponse<Client>> =>
      this.request<ApiResponse<Client>>(`/clients/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      }),

    toggleStatus: (id: string): Promise<ApiResponse<Client>> =>
      this.request<ApiResponse<Client>>(`/clients/${id}/toggle-status`, {
        method: 'PATCH'
      }),

    delete: (id: string): Promise<ApiResponse<void>> =>
      this.request<ApiResponse<void>>(`/clients/${id}`, {
        method: 'DELETE'
      }),

    regenerateUninstallPassword: (id: string): Promise<ApiResponse<{ password: string }>> =>
      this.request<ApiResponse<{ password: string }>>(`/clients/${id}/regenerate-uninstall-password`, {
        method: 'POST'
      })
  };

  // Device endpoints
  devices = {
    getAll: (options?: FilterOptions): Promise<ApiResponse<DeviceListResponse>> =>
      this.request<ApiResponse<DeviceListResponse>>(`/devices${buildQueryString(options)}`),

    getById: (id: string): Promise<ApiResponse<Device>> =>
      this.request<ApiResponse<Device>>(`/devices/${id}`),

    getUnlinked: (): Promise<ApiResponse<DeviceSummary[]>> =>
      this.request<ApiResponse<DeviceSummary[]>>('/devices/unlinked'),

    linkToClient: (deviceId: string, clientId: string, responsibleUser: string): Promise<ApiResponse<DeviceSummary>> =>
      this.request<ApiResponse<DeviceSummary>>(`/devices/${deviceId}/link`, {
        method: 'PATCH',
        body: JSON.stringify({ clientId, responsibleUser } as LinkDeviceDto)
      }),

    unlinkFromClient: (deviceId: string, responsibleUser: string): Promise<ApiResponse<DeviceSummary>> =>
      this.request<ApiResponse<DeviceSummary>>(`/devices/${deviceId}/unlink`, {
        method: 'PATCH',
        body: JSON.stringify({ responsibleUser })
      }),

    linkBatch: (deviceIds: string[], clientId: string, responsibleUser: string): Promise<ApiResponse<LinkingResultDto>> =>
      this.request<ApiResponse<LinkingResultDto>>('/devices/link-batch', {
        method: 'POST',
        body: JSON.stringify({ deviceIds, clientId, responsibleUser } as LinkDevicesBatchDto)
      }),

    unlinkBatch: (deviceIds: string[], responsibleUser: string): Promise<ApiResponse<LinkingResultDto>> =>
      this.request<ApiResponse<LinkingResultDto>>('/devices/unlink-batch', {
        method: 'POST',
        body: JSON.stringify({ deviceIds, responsibleUser } as UnlinkDevicesBatchDto)
      }),

    getByGroup: (groupId: string, options?: FilterOptions): Promise<ApiResponse<DeviceListResponse>> =>
      this.request<ApiResponse<DeviceListResponse>>(`/device-groups/${groupId}/devices${buildQueryString(options)}`),

    delete: (deviceId: string): Promise<ApiResponse<void>> =>
      this.request<ApiResponse<void>>(`/devices/${deviceId}`, {
        method: 'DELETE'
      })
  };

  reports = createReportsApi(this.request.bind(this), buildQueryString);

  // User endpoints
  users = {
    getAll: (options?: FilterOptions): Promise<ApiResponse<UserSummary[]>> =>
      this.request<ApiResponse<UserSummary[]>>(`/users${buildQueryString(options)}`),

    getById: (id: string): Promise<ApiResponse<User>> =>
      this.request<ApiResponse<User>>(`/users/${id}`),

    create: (userData: CreateUserDto): Promise<ApiResponse<User>> =>
      this.request<ApiResponse<User>>('/users', {
        method: 'POST',
        body: JSON.stringify(userData)
      }),

    update: (id: string, updates: UpdateUserDto): Promise<ApiResponse<User>> =>
      this.request<ApiResponse<User>>(`/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      }),

    toggleStatus: (id: string): Promise<ApiResponse<User>> =>
      this.request<ApiResponse<User>>(`/users/${id}/toggle-status`, {
        method: 'PATCH'
      }),

    updatePreferences: (id: string, preferences: { theme?: string; sidebarPreference?: string; preferredLanguage?: string }): Promise<ApiResponse<User>> =>
      this.request<ApiResponse<User>>(`/users/${id}/preferences`, {
        method: 'PATCH',
        body: JSON.stringify(preferences)
      }),

    delete: (id: string): Promise<ApiResponse<void>> =>
      this.request<ApiResponse<void>>(`/users/${id}`, {
        method: 'DELETE'
      }),

    resendInvite: (id: string): Promise<ApiResponse<void>> =>
      this.request<ApiResponse<void>>(`/users/${id}/resend-invite`, {
        method: 'POST'
      }),

    getTeam: (options?: FilterOptions): Promise<ApiResponse<PaginatedUsers>> =>
      this.request<ApiResponse<PaginatedUsers>>(`/users/team${buildQueryString(options)}`),

    getAccountsSummary: (): Promise<ApiResponse<AccountUsersSummary[]>> =>
      this.request<ApiResponse<AccountUsersSummary[]>>('/users/accounts-summary'),

    getByAccountId: (accountId: string, options?: FilterOptions): Promise<ApiResponse<PaginatedUsers>> =>
      this.request<ApiResponse<PaginatedUsers>>(`/users/by-account/${accountId}${buildQueryString(options)}`)
  };

  // Alert endpoints
  alerts = {
    getAll: (options?: FilterOptions): Promise<ApiResponse<AlertsResponse>> =>
      this.request<ApiResponse<AlertsResponse>>(`/alerts${buildQueryString(options)}`),

    getByClientId: (clientId: string, options?: FilterOptions): Promise<ApiResponse<AlertsResponse>> =>
      this.request<ApiResponse<AlertsResponse>>(`/alerts/client/${clientId}${buildQueryString(options)}`),

    getByGroupId: (groupId: string, options?: FilterOptions): Promise<ApiResponse<AlertsResponse>> =>
      this.request<ApiResponse<AlertsResponse>>(`/alerts/group/${groupId}${buildQueryString(options)}`),

    getSummary: (): Promise<ApiResponse<AlertsSummary>> =>
      this.request<ApiResponse<AlertsSummary>>('/alerts/summary'),

    create: (alertData: CreateAlertDto): Promise<ApiResponse<Alert>> =>
      this.request<ApiResponse<Alert>>('/alerts', {
        method: 'POST',
        body: JSON.stringify(alertData)
      }),

    getRule: (id: number): Promise<ApiResponse<Record<string, unknown>>> =>
      this.request<ApiResponse<Record<string, unknown>>>(`/alerts/rules/${id}`),

    createRule: (ruleData: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> =>
      this.request<ApiResponse<Record<string, unknown>>>('/alerts/rules', {
        method: 'POST',
        body: JSON.stringify(ruleData)
      })
  };

  // Dashboard endpoints
  dashboard = {
    getOverview: (): Promise<ApiResponse<DashboardStats>> =>
      this.request<ApiResponse<DashboardStats>>('/dashboard/overview'),

    getDevicesStatus: (): Promise<ApiResponse<Record<string, unknown>>> =>
      this.request<ApiResponse<Record<string, unknown>>>('/dashboard/devices-status'),

    getRecentAlerts: (params?: { page?: number; pageSize?: number; severity?: string; status?: string; hours?: number }): Promise<ApiResponse<{ alerts: Alert[]; totalCount: number; currentPage: number; pageSize: number }>> => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.append('page', String(params.page));
      if (params?.pageSize) searchParams.append('pageSize', String(params.pageSize));
      if (params?.severity && params.severity !== 'all') searchParams.append('severity', params.severity);
      if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
      if (params?.hours !== undefined) searchParams.append('hours', String(params.hours));
      const queryString = searchParams.toString();
      return this.request<ApiResponse<{ alerts: Alert[]; totalCount: number; currentPage: number; pageSize: number }>>(`/dashboard/recent-alerts${queryString ? `?${queryString}` : ''}`);
    },

    getClientsDevices: (): Promise<ApiResponse<ClientSummary[]>> =>
      this.request<ApiResponse<ClientSummary[]>>('/dashboard/clients-devices')
  };

  // Installation Token endpoints
  installationTokens = {
    create: (data: CreateInstallationTokenDto): Promise<ApiResponse<InstallationToken>> =>
      this.request<ApiResponse<InstallationToken>>('/installation-tokens', {
        method: 'POST',
        body: JSON.stringify(data)
      }),

    getAll: (params?: InstallationTokenListParams): Promise<ApiResponse<InstallationTokenListResponse>> => {
      const searchParams = new URLSearchParams();
      if (params?.clientId) searchParams.append('clientId', params.clientId);
      if (params?.search) searchParams.append('search', params.search);
      if (params?.status) searchParams.append('status', params.status);
      if (params?.page) searchParams.append('page', String(params.page));
      if (params?.pageSize) searchParams.append('pageSize', String(params.pageSize));
      const qs = searchParams.toString();
      return this.request<ApiResponse<InstallationTokenListResponse>>(
        `/installation-tokens${qs ? `?${qs}` : ''}`
      );
    },

    deactivate: (id: string): Promise<ApiResponse<void>> =>
      this.request<ApiResponse<void>>(`/installation-tokens/${id}/deactivate`, {
        method: 'PATCH'
      }),

    activate: (id: string): Promise<ApiResponse<void>> =>
      this.request<ApiResponse<void>>(`/installation-tokens/${id}/activate`, {
        method: 'PATCH'
      }),

    regenerate: (id: string): Promise<ApiResponse<void>> =>
      this.request<ApiResponse<void>>(`/installation-tokens/${id}/regenerate`, {
        method: 'POST'
      }),

    update: (
      id: string,
      data: { maxUses?: number; expiresAt?: string; clearExpiresAt?: boolean }
    ): Promise<ApiResponse<InstallationToken>> =>
      this.request<ApiResponse<InstallationToken>>(`/installation-tokens/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      }),

    getLicenses: (id: string, page = 1, pageSize = 10): Promise<ApiResponse<DeviceLicensesResponse>> =>
      this.request<ApiResponse<DeviceLicensesResponse>>(`/installation-tokens/${id}/licenses?page=${page}&pageSize=${pageSize}`),

    delete: (id: string): Promise<ApiResponse<void>> =>
      this.request<ApiResponse<void>>(`/installation-tokens/${id}`, {
        method: 'DELETE'
      })
  };

  // Inventory endpoints
  inventory = {
    getAll: (options?: FilterOptions): Promise<ApiResponse<DeviceInventoryResponse>> =>
      this.request<ApiResponse<DeviceInventoryResponse>>(`/inventory${buildQueryString(options)}`),

    syncAll: (): Promise<ApiResponse<Record<string, unknown>>> =>
      this.request<ApiResponse<Record<string, unknown>>>('/inventory/sync', {
        method: 'POST'
      })
  };
}

export const apiClient = new ApiClient(getApiBaseUrl);

export type {
  Client,
  ClientSummary,
  Device,
  DeviceSummary,
  DeviceListResponse,
  DeviceInventoryResponse,
  User,
  UserSummary,
  AccountUsersSummary,
  PaginatedUsers,
  Alert,
  AlertsResponse,
  AlertsSummary,
  DashboardStats,
  CreateClientDto,
  UpdateClientDto,
  CreateUserDto,
  UpdateUserDto,
  CreateAlertDto,
  LinkingResultDto,
  ApiResponse,
  FilterOptions,
  InstallationToken,
  InstallationTokenListResponse,
  InstallationTokenListParams,
  InstallationTokenStatusFilter,
  CreateInstallationTokenDto,
  DeviceLicense,
  DeviceLicensesResponse
};
